import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Zap, Link2, LayoutTemplate, BarChart3, Timer, ShieldCheck, Code2, ArrowRight, Copy } from "lucide-react";

const features = [
  { icon: Zap, title: "Ultra Hızlı Yönlendirme", desc: "Optimize edilmiş redirect ve önbellek ile milisaniyeler içinde açılır." },
  { icon: LayoutTemplate, title: "Mini Site Oluşturucu", desc: "Hazır şablon veya kendi HTML kodunuzla saniyeler içinde sayfa yayınlayın." },
  { icon: BarChart3, title: "Tıklanma İstatistikleri", desc: "Her linkin performansını canlı grafiklerle takip edin." },
  { icon: Code2, title: "OG & Sosyal Meta", desc: "Her link için OG title, description ve görsel yönetin." },
  { icon: Timer, title: "4 Saniyelik Ara Sayfa", desc: "Reklam destekli, atlanamayan modern geri sayım sayfası." },
  { icon: ShieldCheck, title: "Üretim Seviyesi Güvenlik", desc: "Rate limiting, brute-force koruması ve güvenli redirect." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [url, setUrl] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "kisalink.net — Ultra Hızlı Link Kısaltma & Mini Site Oluşturucu";
    api.get("/public/settings").then(({ data }) => {
      if (data.site_description) {
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
        meta.content = data.site_description;
      }
    }).catch(() => {});
  }, []);

  const handleShorten = async () => {
    if (!url.trim()) return toast.error("Lütfen bir URL girin");
    if (!user) {
      toast.info("Link oluşturmak için giriş yapın");
      navigate("/register");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/links", { target_url: url.trim() });
      const short = `${window.location.origin}/${data.code}`;
      setResult(short);
      toast.success("Kısa link oluşturuldu!");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-14 sm:pt-24 sm:pb-20">
          <div className="max-w-3xl kl-fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] font-display">
              Linklerinizi kısaltın,<br /><span className="text-blue-600">mini siteler</span> oluşturun.
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl">
              kisalink.net ile uzun bağlantıları saniyeler içinde kısaltın, tıklanma istatistiklerini takip edin ve profesyonel bio-link sayfaları yayınlayın.
            </p>
          </div>

          <Card className="mt-9 p-2 sm:p-2.5 max-w-2xl rounded-2xl shadow-xl shadow-blue-900/5 border-slate-200 kl-fade-up">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 flex items-center gap-2 px-3">
                <Link2 className="w-5 h-5 text-slate-400 shrink-0" />
                <Input
                  data-testid="shorten-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleShorten()}
                  placeholder="https://cok-uzun-bir-baglanti.com/..."
                  className="border-0 shadow-none focus-visible:ring-0 text-base h-12"
                />
              </div>
              <Button data-testid="shorten-submit-button" onClick={handleShorten} disabled={loading} className="h-12 px-6 bg-blue-600 hover:bg-blue-700 rounded-xl text-base">
                {loading ? "..." : "Kısalt"} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </Card>

          {result && (
            <div data-testid="shorten-result" className="mt-4 max-w-2xl flex items-center gap-3 bg-white border border-blue-200 rounded-xl px-4 py-3">
              <span className="font-mono text-blue-700 font-semibold truncate">{result}</span>
              <Button variant="outline" size="sm" className="ml-auto shrink-0" onClick={() => { navigator.clipboard.writeText(result); toast.success("Kopyalandı!"); }}>
                <Copy className="w-4 h-4 mr-1" /> Kopyala
              </Button>
            </div>
          )}

          <div className="mt-6 flex items-center gap-6 text-sm text-slate-500">
            <span>✓ Kredi kartı gerekmez</span>
            <span>✓ Sınırsız link</span>
            <span>✓ Anlık istatistik</span>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-2xl">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-display">Bir platform, tüm ihtiyaçlarınız</h2>
          <p className="mt-3 text-slate-600">Link kısaltmadan mini site oluşturmaya, istatistikten reklam yönetimine kadar her şey tek yerde.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <Card key={i} className="p-6 rounded-2xl border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <span className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                <f.icon className="w-5 h-5 text-blue-600" />
              </span>
              <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="bg-blue-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-display">Bugün ücretsiz başlayın</h2>
          <p className="mt-3 text-blue-100 max-w-lg mx-auto">Saniyeler içinde hesap oluşturun, ilk kısa linkinizi ve mini sitenizi yayınlayın.</p>
          <Button size="lg" onClick={() => navigate("/register")} className="mt-7 bg-white text-blue-700 hover:bg-blue-50 rounded-xl" data-testid="cta-register">
            Hesap Oluştur <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-sm text-slate-500 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} kisalink.net — Tüm hakları saklıdır.</span>
          <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-blue-600" /> Modern link platformu</span>
        </div>
      </footer>
    </div>
  );
}
